import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { ProductTier, ProductTier_index } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/product.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { useIsCallIntelEligibleAccount } from './use-is-call-intel-eligible-account';

type UseCallIntelSubscriptionParams = {
  includeAiReceptionist?: boolean;
  locationIds?: string[];
};

type UseCallIntelSubscription = {
  currentPlan: ProductTier;
  locationPlanMap: Record<string, ProductTier>;
  callIntelBasePlanLocationIds: string[];
  callIntelProPlanLocationIds: string[];
  trialActiveLocationIds: string[];
  isCallIntelBaseActive: boolean;
  isCallIntelProActive: boolean;
  isCallIntelUpgradeEligible: boolean;
  isCallIntelFreeTrialEligible: boolean;
  isCallIntelActive: boolean;
  isLoading: boolean;
  trialDateRangeMap: Record<string, { startDate?: string; endDate?: string }>;
};

const getHighestPlan = (plans: ProductTier[]): ProductTier => {
  if (plans.length === 0) {
    return ProductTier.PRODUCT_TIER_UNSPECIFIED;
  }

  return plans.reduce<ProductTier>((highestPlan, plan) => {
    return ProductTier_index[plan] > ProductTier_index[highestPlan] ? plan : highestPlan;
  }, ProductTier.PRODUCT_TIER_UNSPECIFIED);
};

export const useCallIntelSubscription = ({
  includeAiReceptionist = true,
  locationIds,
}: UseCallIntelSubscriptionParams = {}): UseCallIntelSubscription => {
  const { selectedLocationIds: appScopeLocationIds } = useAppScopeStore();
  const selectedLocationIds = locationIds || appScopeLocationIds;

  const {
    activeLocationIds,
    isCallIntelUpgradeEligible,
    isCallIntelFreeTrialEligible,
    trialActiveLocationIds,
    isLoading: isEligibilityLoading,
    trialDateRangeMap,
  } = useIsCallIntelEligibleAccount({
    includeAiReceptionist,
    locationIds: selectedLocationIds,
  });

  const callIntelProPlanLocationIds = useMemo(() => [...activeLocationIds], [activeLocationIds]);

  const locationsAlreadyAssignedHigherPlan = useMemo(() => {
    return new Set(callIntelProPlanLocationIds);
  }, [callIntelProPlanLocationIds]);

  const locationsNeedingPlanLookup = useMemo(() => {
    return (selectedLocationIds || []).filter((locationId) => !locationsAlreadyAssignedHigherPlan.has(locationId));
  }, [locationsAlreadyAssignedHigherPlan, selectedLocationIds]);

  const { getCustomizationFlagLocationMap } = useAppFlagStore();

  const callIntelBasePlanLocationIds = useMemo(() => {
    const flagLocationMap = getCustomizationFlagLocationMap({
      locationIds: locationsNeedingPlanLookup,
      featureEnum: Feature.CALL_INTELLIGENCE_STANDARD,
    });

    return locationsNeedingPlanLookup.filter((locationId) => {
      const flagData = flagLocationMap[locationId];
      return flagData?.state === State.ACTIVE;
    });
  }, [getCustomizationFlagLocationMap, locationsNeedingPlanLookup]);

  const locationPlanMap = useMemo(() => {
    const proPlanLocations = new Set(callIntelProPlanLocationIds);
    const basePlanLocations = new Set(callIntelBasePlanLocationIds);

    return (selectedLocationIds || []).reduce<Record<string, ProductTier>>((acc, locationId) => {
      if (proPlanLocations.has(locationId)) {
        acc[locationId] = ProductTier.PRODUCT_TIER_PRO;
      } else if (basePlanLocations.has(locationId)) {
        acc[locationId] = ProductTier.PRODUCT_TIER_BASE;
      } else {
        acc[locationId] = ProductTier.PRODUCT_TIER_UNSPECIFIED;
      }

      return acc;
    }, {});
  }, [callIntelBasePlanLocationIds, callIntelProPlanLocationIds, selectedLocationIds]);

  const currentPlan = useMemo(() => {
    return getHighestPlan(Object.values(locationPlanMap));
  }, [locationPlanMap]);

  const isCallIntelProActive = callIntelProPlanLocationIds.length > 0;
  const isCallIntelBaseActive = callIntelBasePlanLocationIds.length > 0;
  const isCallIntelActive = isCallIntelProActive || isCallIntelBaseActive;

  return {
    currentPlan,
    locationPlanMap,
    callIntelBasePlanLocationIds,
    callIntelProPlanLocationIds,
    trialActiveLocationIds,
    isCallIntelBaseActive,
    isCallIntelProActive,
    isCallIntelUpgradeEligible,
    isCallIntelFreeTrialEligible,
    isCallIntelActive,
    isLoading: isEligibilityLoading,
    trialDateRangeMap,
  };
};
